import { useCallback, useEffect, useState, useContext } from "react";
import ProfileNavigation from "../../../components/common/ProfileNavigation";
import SEO from "../../../components/SEO";
import useUser, { deleteAuthorized, getAuthorized, isUserClient, postAuthorized } from "../../../hooks/User";
import { useNavigate } from "react-router-dom";
import { ErrorHandlerContext, ErrorHandlerContextProvider } from "../../../contexts/ErrorHandlerContext";
import ErrorHandler from "../../../components/common/ErrorHandler";
import { ROUTES } from "../../../routes";
import { isLikelyOfflineOrNetworkError } from "../../../utils/offlineError";

import "./doctors.css";

// Icons
const UserIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
        <circle cx="12" cy="7" r="4"/>
    </svg>
);

const UsersIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <path d="M22 21v-2a4 4 0 0 0-3-3.87"/>
        <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
);

const UserPlusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <line x1="19" y1="8" x2="19" y2="14"/>
        <line x1="22" y1="11" x2="16" y2="11"/>
    </svg>
);

const UserMinusIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/>
        <circle cx="9" cy="7" r="4"/>
        <line x1="22" y1="11" x2="16" y2="11"/>
    </svg>
);

const SearchIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.3-4.3"/>
    </svg>
);

const MailIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect width="20" height="16" x="2" y="4" rx="2"/>
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
);

const CheckIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="20 6 9 17 4 12"/>
    </svg>
);

const ChevronLeftIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m15 18-6-6 6-6"/>
    </svg>
);

const ChevronRightIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m9 18 6-6-6-6"/>
    </svg>
);

const AlertTriangleIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
        <line x1="12" y1="9" x2="12" y2="13"/>
        <line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
);

const XIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 6 6 18"/>
        <path d="m6 6 12 12"/>
    </svg>
);

const DEFAULT_PAGE_SIZE = 10;

// Confirm Modal Component
function ConfirmModal({ isOpen, onClose, onConfirm, title, message, doctorName, confirmText, isLoading, type = "add" }) {
    if (!isOpen) return null;
    
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
                <div className="confirm-icon">
                    <AlertTriangleIcon />
                </div>
                <h3>{title}</h3>
                <p>{message}</p>
                {doctorName && <p className="confirm-doctor-name">{doctorName}</p>}
                
                <div className="confirm-actions">
                    <button className="btn-secondary" onClick={onClose} disabled={isLoading}>
                        Отмена
                    </button>
                    <button 
                        className={`btn-confirm ${type === 'add' ? 'btn-confirm-add' : 'btn-confirm-remove'}`} 
                        onClick={onConfirm}
                        disabled={isLoading}
                    >
                        {isLoading ? "Загрузка..." : confirmText}
                    </button>
                </div>
            </div>
        </div>
    );
}

// Doctor Card Component
function DoctorCard({ doctor, isMyDoctor, onAction, isLoading }) {
    const displayName = doctor.full_name || doctor.username || doctor.email || "Врач";
    const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    
    return (
        <div className="doctor-card">
            <div className="doctor-avatar">
                {initials || <UserIcon />}
            </div>
            
            <div className="doctor-info">
                <h3 className="doctor-name" title={displayName}>{displayName}</h3>
                {doctor.email && (
                    <div className="doctor-email">
                        <MailIcon />
                        <span title={doctor.email}>{doctor.email}</span>
                    </div>
                )}

            </div>
            
            <button 
                className={`btn-action ${isMyDoctor ? 'btn-remove' : 'btn-add'}`}
                onClick={() => onAction(doctor)}
                disabled={isLoading}
            >
                {isLoading ? (
                    <span className="loading-dots">...</span>
                ) : (
                    <>
                        {isMyDoctor ? <UserMinusIcon /> : <UserPlusIcon />}
                        <span>{isMyDoctor ? "Открепиться" : "Закрепиться"}</span>
                    </>
                )}
            </button>
        </div>
    );
}

// Pagination Component
function Pagination({ currentPage, totalPages, onPageChange, totalItems, pageSize }) {
    if (totalPages <= 1) return null;

    const startItem = (currentPage - 1) * pageSize + 1;
    const endItem = Math.min(currentPage * pageSize, totalItems);

    return (
        <div className="pagination">
            <div className="pagination-info">
                {startItem}-{endItem} из {totalItems}
            </div>
            <div className="pagination-controls">
                <button 
                    className="pagination-btn"
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    title="Предыдущая страница"
                >
                    <ChevronLeftIcon />
                </button>
                
                <span className="pagination-current">
                    {currentPage} / {totalPages}
                </span>
                
                <button 
                    className="pagination-btn"
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    title="Следующая страница"
                >
                    <ChevronRightIcon />
                </button>
            </div>
        </div>
    );
}

// Tab Button Component
function TabButton({ active, onClick, icon: Icon, label, count }) {
    return (
        <button 
            className={`tab-btn ${active ? 'active' : ''}`}
            onClick={onClick}
        >
            <Icon />
            <span>{label}</span>
            {count !== undefined && <span className="tab-count">{count}</span>}
        </button>
    );
}

// Main Component
function Doctors() {
    const { data: userData, loading: userLoading, error: userError } = useUser();
    const navigate = useNavigate();
    const { setErrorList } = useContext(ErrorHandlerContext);
    
    const [activeTab, setActiveTab] = useState("available"); // "available" | "my"
    
    // My doctors state
    const [myDoctors, setMyDoctors] = useState([]);
    const [myDoctorsLoading, setMyDoctorsLoading] = useState(true);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    
    // Confirm modal state
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        type: null, // 'add' or 'remove'
        doctor: null
    });
    
    // Available doctors state
    const [availableDoctors, setAvailableDoctors] = useState([]);
    const [availableTotal, setAvailableTotal] = useState(0);
    const [availableLoading, setAvailableLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    
    // Auth check
    useEffect(() => {
        if (userLoading) return;
        if (userError) {
            navigate(ROUTES.AUTH, { viewTransition: true });
            return;
        }
        // Разрешаем доступ всем пользователям с ролью client (включая врачей)
        if (userData && !isUserClient(userData)) {
            navigate(ROUTES.PROFILE, { viewTransition: true });
        }
    }, [userLoading, userError, userData, navigate]);
    
    // Debounce search
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);
    
    // Load my doctors
    const loadMyDoctors = useCallback(async () => {
        setMyDoctorsLoading(true);
        try {
            const response = await getAuthorized("/client/my_doctors");
            setMyDoctors(response.data || []);
        } catch (error) {
            console.error("Failed to load my doctors:", error);
            // При офлайне этот экран не критичен — не спамим дублирующими уведомлениями.
            if (!isLikelyOfflineOrNetworkError(error)) {
                setErrorList(prev => [...prev, "Ошибка при загрузке моих врачей"]);
            }
            setMyDoctors([]);
        } finally {
            setMyDoctorsLoading(false);
        }
    }, [setErrorList]);
    
    // Load available doctors
    const loadAvailableDoctors = useCallback(async () => {
        setAvailableLoading(true);
        try {
            const st = (currentPage - 1) * DEFAULT_PAGE_SIZE;
            const params = {
                st,
                limit: DEFAULT_PAGE_SIZE
            };
            if (debouncedSearch.trim()) {
                params.query = debouncedSearch.trim();
            }
            
            const response = await getAuthorized("/client/available_doctors", { params });
            setAvailableDoctors(response.data?.doctors || []);
            setAvailableTotal(response.data?.total_count || 0);
        } catch (error) {
            console.error("Failed to load available doctors:", error);
            // При офлайне этот экран не критичен — не спамим дублирующими уведомлениями.
            if (!isLikelyOfflineOrNetworkError(error)) {
                setErrorList(prev => [...prev, "Ошибка при загрузке доступных врачей"]);
            }
            setAvailableDoctors([]);
            setAvailableTotal(0);
        } finally {
            setAvailableLoading(false);
        }
    }, [currentPage, debouncedSearch, setErrorList]);
    
    // Load my doctors immediately on mount (for count display)
    useEffect(() => {
        if (userLoading) return;
        loadMyDoctors();
    }, [userLoading, loadMyDoctors]);
    
    // Load data when tab changes or search/page changes
    useEffect(() => {
        if (userLoading) return;
        if (activeTab === "available") {
            loadAvailableDoctors();
        } else if (activeTab === "my") {
            loadMyDoctors();
        }
    }, [activeTab, loadAvailableDoctors, loadMyDoctors, userLoading]);
    
    // Handle action click - open confirm modal
    const handleActionClick = useCallback((doctor, type) => {
        setConfirmModal({
            isOpen: true,
            type,
            doctor
        });
    }, []);
    
    // Handle confirm modal close
    const handleConfirmClose = useCallback(() => {
        setConfirmModal({ isOpen: false, type: null, doctor: null });
    }, []);
    
    // Handle associate
    const handleAssociate = useCallback(async () => {
        const doctor = confirmModal.doctor;
        if (!doctor) return;
        
        setActionLoadingId(doctor.id);
        try {
            await postAuthorized("/client/doctor_association", { doctor_id: doctor.id });
            setConfirmModal({ isOpen: false, type: null, doctor: null });
            // Refresh available doctors and switch to my doctors
            await loadAvailableDoctors();
            setActiveTab("my");
        } catch (error) {
            console.error("Failed to associate doctor:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при закреплении за врачом"]);
        } finally {
            setActionLoadingId(null);
        }
    }, [confirmModal.doctor, loadAvailableDoctors, setErrorList]);
    
    // Handle disassociate
    const handleDisassociate = useCallback(async () => {
        const doctor = confirmModal.doctor;
        if (!doctor) return;
        
        setActionLoadingId(doctor.id);
        try {
            await deleteAuthorized("/client/doctor_disassociation", { doctor_id: doctor.id });
            setConfirmModal({ isOpen: false, type: null, doctor: null });
            loadMyDoctors();
        } catch (error) {
            console.error("Failed to disassociate doctor:", error);
            setErrorList(prev => [...prev, error.response?.data?.error_description || "Ошибка при откреплении от врача"]);
        } finally {
            setActionLoadingId(null);
        }
    }, [confirmModal.doctor, loadMyDoctors, setErrorList]);
    
    const totalPages = Math.ceil(availableTotal / DEFAULT_PAGE_SIZE);
    
    // Loading state
    if (userLoading) {
        return (
            <div className="doctors-page">
                <ProfileNavigation />
                <div className="doctors-loading">
                    <div className="spinner"></div>
                    <p>Загрузка...</p>
                </div>
            </div>
        );
    }
    
    const isLoading = activeTab === "my" ? myDoctorsLoading : availableLoading;
    const doctors = activeTab === "my" ? myDoctors : availableDoctors;
    const totalCount = activeTab === "my" ? myDoctors.length : availableTotal;
    
    return (
        <div className="doctors-page">
            <ProfileNavigation />
            
            <div className="doctors-container">
                {/* Header */}
                <div className="doctors-header">
                    <div className="doctors-title">
                        <UsersIcon />
                        <div>
                            <h1>Мои врачи</h1>
                            <p className="doctors-subtitle">Управление привязкой к врачам</p>
                        </div>
                    </div>
                </div>
                
                {/* Tabs */}
                <div className="doctors-tabs">
                    <TabButton 
                        active={activeTab === "available"}
                        onClick={() => setActiveTab("available")}
                        icon={UserPlusIcon}
                        label="Доступные врачи"
                    />
                    <TabButton 
                        active={activeTab === "my"}
                        onClick={() => setActiveTab("my")}
                        icon={UsersIcon}
                        label="Мои врачи"
                        count={myDoctors.length}
                    />
                </div>
                
                {/* Search (only for available doctors) */}
                {activeTab === "available" && (
                    <div className="doctors-search">
                        <div className="search-box">
                            <SearchIcon />
                            <input
                                type="text"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                placeholder="Поиск по имени или email..."
                            />
                            {searchQuery && (
                                <button 
                                    className="search-clear"
                                    onClick={() => setSearchQuery("")}
                                    title="Очистить поиск"
                                >
                                    ×
                                </button>
                            )}
                        </div>
                        {debouncedSearch && (
                            <div className="search-results-info">
                                Найдено: {availableTotal}
                            </div>
                        )}
                    </div>
                )}
                
                {/* Content */}
                {isLoading ? (
                    <div className="doctors-loading">
                        <div className="spinner"></div>
                        <p>Загрузка врачей...</p>
                    </div>
                ) : doctors.length === 0 ? (
                    <div className="empty-doctors-state">
                        <div className="empty-icon">
                            {activeTab === "my" ? <UsersIcon /> : <UserPlusIcon />}
                        </div>
                        <h3>
                            {activeTab === "my" 
                                ? "У вас нет привязанных врачей" 
                                : (debouncedSearch ? "Врачи не найдены" : "Нет доступных врачей")
                            }
                        </h3>
                        <p>
                            {activeTab === "my"
                                ? "Перейдите во вкладку \"Доступные врачи\" чтобы привязаться к врачу"
                                : (debouncedSearch ? "Попробуйте изменить параметры поиска" : "")
                            }
                        </p>
                    </div>
                ) : (
                    <>
                        <div className="doctors-stats">
                            <span>Всего: {totalCount}</span>
                        </div>
                        
                        <div className="doctors-grid">
                            {doctors.map((doctor) => (
                                <DoctorCard
                                    key={doctor.id}
                                    doctor={doctor}
                                    isMyDoctor={activeTab === "my"}
                                    onAction={(doc) => handleActionClick(doc, activeTab === "my" ? 'remove' : 'add')}
                                    isLoading={actionLoadingId === doctor.id}
                                />
                            ))}
                        </div>
                        
                        {activeTab === "available" && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={setCurrentPage}
                                totalItems={availableTotal}
                                pageSize={DEFAULT_PAGE_SIZE}
                            />
                        )}
                    </>
                )}
                
                {/* Confirm Modal */}
                <ConfirmModal
                    isOpen={confirmModal.isOpen}
                    onClose={handleConfirmClose}
                    onConfirm={confirmModal.type === 'add' ? handleAssociate : handleDisassociate}
                    title={confirmModal.type === 'add' ? 'Закрепиться за врачом?' : 'Открепиться от врача?'}
                    message={confirmModal.type === 'add' 
                        ? 'Вы уверены, что хотите закрепиться за этим врачом?' 
                        : 'Вы уверены, что хотите открепиться от этого врача?'}
                    doctorName={confirmModal.doctor?.full_name || confirmModal.doctor?.email}
                    confirmText={confirmModal.type === 'add' ? 'Закрепиться' : 'Открепиться'}
                    isLoading={actionLoadingId === confirmModal.doctor?.id}
                    type={confirmModal.type}
                />
            </div>
        </div>
    );
}

// Export wrapped with ErrorHandler
export default function DoctorsPage() {
    return (
        <>
            <SEO 
                title="Мои врачи" 
                description="Управление списком ваших врачей и специалистов" 
                noindex={true} 
            />
            <ErrorHandlerContextProvider>
                <Doctors />
                <ErrorHandler />
            </ErrorHandlerContextProvider>
        </>
    );
}
